/**
 * Vorgangsseite, Aktionskarte: „In OpenTable eintragen“ (Antoine, 26.10.2026).
 *
 * Zeigt nur an, was der Server entschieden hat (`GET /api/events/:id/opentable`). Der Link öffnet
 * IMMER in einem neuen Tab (`target="_blank"`, `rel="noopener noreferrer"`), auch der Knopf je Tag.
 * MAESTRO schreibt nichts in OpenTable: „Als eingetragen markieren“ ist die Aussage des Menschen,
 * dass er es dort eingetragen hat.
 */
import { useState } from "react";
import { Button, ButtonLink, Icon } from "./components";
import { formatDateTime } from "./format";
import { datumDe, gastdatenText, type GastdatenEingabe, type OpentableKnopf, type OpentableSicht } from "../lib/opentable";

async function inZwischenablage(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* weiter mit dem Ersatzweg */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function beschriftung(k: OpentableKnopf, mehrere: boolean): string {
  if (!mehrere) return "In OpenTable eintragen";
  const teile = [datumDe(k.datum), k.gaeste != null ? `${k.gaeste} Gäste` : null].filter(Boolean).join(", ");
  return teile ? `In OpenTable eintragen · ${teile}` : "In OpenTable eintragen";
}

export function OpenTableAktion({
  sicht,
  gast,
  busy,
  fehler,
  onMarkieren,
}: {
  sicht: OpentableSicht | null;
  gast: GastdatenEingabe;
  busy: boolean;
  fehler: string | null;
  onMarkieren: (eingetragen: boolean) => void;
}) {
  const [kopiert, setKopiert] = useState<"ja" | "fehler" | null>(null);
  if (!sicht || !sicht.sichtbar) return null;
  const mehrere = sicht.knoepfe.length > 1;
  const eingetragen = sicht.eingetragen;

  async function kopieren() {
    const ok = await inZwischenablage(gastdatenText(gast));
    setKopiert(ok ? "ja" : "fehler");
    window.setTimeout(() => setKopiert(null), 2500);
  }

  return (
    <div className="mt-1 flex w-full basis-full flex-col gap-2.5 border-t border-outline-variant/40 pt-3.5 dark:border-white/10" data-testid="opentable-aktion">
      <p className="text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-secondary dark:text-secondary-fixed-dim">OpenTable</p>
      <div className="flex flex-col gap-2">
        {sicht.knoepfe.map((k, i) => (
          <div key={`${k.datum ?? "x"}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="opentable-zeile">
            {k.zustand === "bereit" && k.link ? (
              <ButtonLink variant="secondary" className="!py-2" href={k.link} target="_blank" rel="noopener noreferrer" data-testid="opentable-knopf">
                <Icon name="open_in_new" className="text-[16px]" /> {beschriftung(k, mehrere)}
              </ButtonLink>
            ) : (
              <Button type="button" variant="secondary" className="!py-2" disabled aria-disabled="true" data-testid="opentable-knopf-ausgegraut">
                <Icon name="open_in_new" className="text-[16px]" /> {beschriftung(k, mehrere)}
              </Button>
            )}
            {k.hinweis && (
              <span className="text-[12.5px] text-secondary dark:text-secondary-fixed-dim" data-testid="opentable-hinweis">
                {k.hinweis}
              </span>
            )}
          </div>
        ))}
      </div>
      {sicht.geaendertSeitEintrag && (
        <p className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#8a5a00] dark:text-[#e9c46a]" data-testid="opentable-geaendert">
          <Icon name="warning" className="text-[16px]" /> Daten seit Eintrag geändert
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" className="!py-2" onClick={() => void kopieren()} data-testid="opentable-gastdaten-kopieren">
          <Icon name={kopiert === "ja" ? "check" : "content_copy"} className="text-[16px]" /> {kopiert === "ja" ? "Kopiert" : "Gastdaten kopieren"}
        </Button>
        {kopiert === "fehler" && <span className="text-[12.5px] text-error">Kopieren hat nicht geklappt.</span>}
        {eingetragen ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-secondary dark:text-secondary-fixed-dim" data-testid="opentable-eingetragen-text">
              <Icon name="check_circle" className="text-[16px] text-tertiary" />
              Eingetragen{eingetragen.von ? ` von ${eingetragen.von}` : ""} am {formatDateTime(eingetragen.am)}
            </span>
            <Button type="button" variant="ghost" className="!py-2" disabled={busy} onClick={() => onMarkieren(false)} data-testid="opentable-rueckgaengig">
              <Icon name="undo" className="text-[16px]" /> Rückgängig
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="!py-2"
            disabled={busy || !sicht.markierbar}
            title={sicht.markierbar ? undefined : "Erst Datum, Uhrzeit und Gästezahl ergänzen"}
            onClick={() => onMarkieren(true)}
            data-testid="opentable-markieren"
          >
            <Icon name="task_alt" className="text-[16px]" /> Als eingetragen markieren
          </Button>
        )}
      </div>
      <p className="text-[12px] text-secondary dark:text-secondary-fixed-dim">
        Name, Mail und Telefon lassen sich bei OpenTable nicht vorbelegen. Die Firma gehört dort ins Feld „Sonderwunsch“.
      </p>
      {fehler && <p className="text-[12.5px] text-error" role="alert">{fehler}</p>}
    </div>
  );
}
